/** Nomads-style quick chips for the profile. The role chips simply fill
 * the free-text role field; the "here for" ids are stored as a list and
 * validated server-side against this exact set. */
export const ROLE_CHIPS = [
  '🚀 Founder',
  '💻 Developer',
  '🎨 Designer',
  '✍️ Writer',
  '📚 Student',
  '📈 Marketer',
  '🔬 Researcher',
  '💼 Consultant',
  '🎬 Creator',
  '🧑‍🏫 Coach',
  '💰 Finance',
  '🖌️ Artist',
] as const

export const HERE_FOR_OPTIONS = [
  { id: 'deep-work', label: '🎯 Deep work' },
  { id: 'studying', label: '📚 Studying' },
  { id: 'accountability', label: '🤝 Accountability' },
  { id: 'meeting-people', label: '☕ Meeting people' },
  { id: 'habit', label: '🌱 Building a habit' },
] as const

export type HereForId = (typeof HERE_FOR_OPTIONS)[number]['id']

export function isHereForId(value: string): value is HereForId {
  return HERE_FOR_OPTIONS.some((option) => option.id === value)
}

export function hereForLabel(id: string): string | null {
  return HERE_FOR_OPTIONS.find((option) => option.id === id)?.label ?? null
}

/** ISO 3166-1 alpha-2 codes for the Nomads-style country dropdowns. Names
 * come from Intl.DisplayNames at runtime; flags from the code points. */
export const COUNTRY_CODES = [
  'AD', 'AE', 'AF', 'AG', 'AL', 'AM', 'AO', 'AR', 'AT', 'AU', 'AZ', 'BA', 'BB', 'BD', 'BE', 'BF', 'BG', 'BH', 'BI',
  'BJ', 'BN', 'BO', 'BR', 'BS', 'BT', 'BW', 'BY', 'BZ', 'CA', 'CD', 'CF', 'CG', 'CH', 'CI', 'CL', 'CM', 'CN', 'CO',
  'CR', 'CU', 'CV', 'CY', 'CZ', 'DE', 'DJ', 'DK', 'DM', 'DO', 'DZ', 'EC', 'EE', 'EG', 'ER', 'ES', 'ET', 'FI', 'FJ',
  'FM', 'FR', 'GA', 'GB', 'GD', 'GE', 'GH', 'GM', 'GN', 'GQ', 'GR', 'GT', 'GW', 'GY', 'HN', 'HR', 'HT', 'HU', 'ID',
  'IE', 'IL', 'IN', 'IQ', 'IR', 'IS', 'IT', 'JM', 'JO', 'JP', 'KE', 'KG', 'KH', 'KI', 'KM', 'KN', 'KP', 'KR', 'KW',
  'KZ', 'LA', 'LB', 'LC', 'LI', 'LK', 'LR', 'LS', 'LT', 'LU', 'LV', 'LY', 'MA', 'MC', 'MD', 'ME', 'MG', 'MH', 'MK',
  'ML', 'MM', 'MN', 'MR', 'MT', 'MU', 'MV', 'MW', 'MX', 'MY', 'MZ', 'NA', 'NE', 'NG', 'NI', 'NL', 'NO', 'NP', 'NR',
  'NZ', 'OM', 'PA', 'PE', 'PG', 'PH', 'PK', 'PL', 'PS', 'PT', 'PW', 'PY', 'QA', 'RO', 'RS', 'RU', 'RW', 'SA', 'SB',
  'SC', 'SD', 'SE', 'SG', 'SI', 'SK', 'SL', 'SM', 'SN', 'SO', 'SR', 'SS', 'ST', 'SV', 'SY', 'SZ', 'TD', 'TG', 'TH',
  'TJ', 'TL', 'TM', 'TN', 'TO', 'TR', 'TT', 'TV', 'TW', 'TZ', 'UA', 'UG', 'US', 'UY', 'UZ', 'VC', 'VE', 'VN', 'VU',
  'WS', 'YE', 'ZA', 'ZM', 'ZW',
] as const

export function isCountryCode(value: string): boolean {
  return (COUNTRY_CODES as readonly string[]).includes(value)
}

export function countryFlag(code: string): string {
  if (!/^[A-Z]{2}$/.test(code)) return ''
  return String.fromCodePoint(...[...code].map((c) => 0x1f1a5 + c.charCodeAt(0)))
}

export function countryName(code: string): string {
  try {
    return new Intl.DisplayNames(['en'], { type: 'region' }).of(code) ?? code
  } catch {
    return code
  }
}
